import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TextInput from '../../components/TextInput';
import TextAreaInput from '../../components/TextAreaInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import roleApi from '../../api/roleApi';
import permissionApi from '../../api/permissionApi';
import { useToast } from '../../contexts/ToastContext';
import CheckboxInput from '../../components/CheckboxInput';

const AddRole = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [],
  });

  useEffect(() => {
    const fetchPermissions = async () => {
      setLoading(true);
      try {
        const response = await permissionApi.getPermissions();
        setPermissions(response.data.map(p => ({ ...p, id: p.id.toString() })));
      } catch (err) {
        console.error('Failed to fetch permissions:', err);
        addToast('Failed to load permissions. Please try again later.', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchPermissions();
  }, [addToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handlePermissionChange = (e, permissionId) => {
    const { checked } = e.target;
    setFormData(prev => {
      const newPermissions = checked
        ? [...prev.permissions, permissionId]
        : prev.permissions.filter(id => id !== permissionId);
      return { ...prev, permissions: newPermissions };
    });
    if (validationErrors.permissions) {
      setValidationErrors(prev => ({ ...prev, permissions: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setValidationErrors({});

    try {
      const payload = {
        ...formData,
        permissions: formData.permissions.map(id => parseInt(id, 10)),
      };
      await roleApi.createRole(payload);
      addToast('Role created successfully!', 'success');
      navigate('/roles');
    } catch (err) {
      console.error('Failed to add Role:', err);
      if (err.response && err.response.status === 422) {
        console.error('Validation Error Response:', err.response.data);
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to add Role. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Add New Role</h1>
      {loading && permissions.length === 0 ? (
        <SkeletonLoader type="form" />
      ) : (
        <form onSubmit={handleSubmit}>
          <TextInput
            label="Role Name"
            name="name"
            id="name"
            value={formData.name}
            onChange={handleChange}
            required={true}
            placeholder="Role Name"
            error={validationErrors?.name?.[0]}
          />
          <TextAreaInput
            label="Description"
            name="description"
            id="description"
            value={formData.description}
            onChange={handleChange}
            required={true}
            rows={4}
            placeholder="Role Description"
            error={validationErrors?.description?.[0]}
          />

          <div className="mb-4">
            <label className="block text-text-gray text-sm font-bold mb-2">Permissions:</label>
            {validationErrors?.permissions && (
              <p className="text-red-500 text-xs italic mb-2">{validationErrors.permissions[0]}</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {permissions.map(permission => {
                const isChecked = formData.permissions.includes(permission.id);
                return (
                  <CheckboxInput
                    key={permission.id}
                    label={permission.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    name="permissions"
                    id={`permission-${permission.id}`}
                    value={permission.id}
                    checked={isChecked ? 1 : 0}
                    onChange={(e) => handlePermissionChange(e, permission.id)}
                  />
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between mt-6">
            <Button
              type="submit"
              className="bg-accent-red hover:bg-red-700 text-white"
              loading={loading}
            >
              Add Role
            </Button>
            <Button
              type="button"
              onClick={() => navigate('/roles')}
              className="bg-gray-500 hover:bg-gray-600 text-white"
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddRole;